import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';

// Camera-style rotating dial navigation for the producer portal (mobile only).
// All menu sections live on a horizontally scrollable "wheel"; the item that
// lands under the center notch becomes the selected window.
const ITEM_W = 76;

const ProducerDialNav = ({ items, badges = {}, checkPermission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollerRef = useRef(null);
  const settleTimer = useRef(null);
  const programmatic = useRef(false);
  const progTimer = useRef(null);
  const lastTickIndex = useRef(-1);
  const [scrollX, setScrollX] = useState(0);

  // Soft haptic tick, like the detents of a real camera mode dial
  const hapticTick = useCallback(() => {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try { navigator.vibrate(8); } catch (e) { /* unsupported */ }
    }
  }, []);

  const matchesPath = useCallback((item) => {
    if (item.path === '/producer/events') {
      return location.pathname === item.path || location.pathname.startsWith('/producer/events/');
    }
    return location.pathname === item.path;
  }, [location.pathname]);

  const activeIndex = items.findIndex(matchesPath);

  const centerItem = useCallback((idx, smooth = true) => {
    const el = scrollerRef.current;
    if (!el || idx < 0) return;
    programmatic.current = true;
    clearTimeout(progTimer.current);
    progTimer.current = setTimeout(() => { programmatic.current = false; }, 800);
    el.scrollTo({ left: idx * ITEM_W, behavior: smooth ? 'smooth' : 'auto' });
    setScrollX(idx * ITEM_W);
  }, []);

  useEffect(() => {
    centerItem(activeIndex >= 0 ? activeIndex : 0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeIndex >= 0) centerItem(activeIndex, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const goTo = useCallback((item) => {
    const allowed = checkPermission ? checkPermission(item) : true;
    if (!allowed || matchesPath(item)) return;
    navigate(item.path);
  }, [checkPermission, matchesPath, navigate]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setScrollX(el.scrollLeft);
    const nearest = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollLeft / ITEM_W)));
    if (nearest !== lastTickIndex.current) {
      if (lastTickIndex.current !== -1 && !programmatic.current) hapticTick();
      lastTickIndex.current = nearest;
    }
    if (programmatic.current) return;
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      const idx = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollLeft / ITEM_W)));
      const item = items[idx];
      if (item) goTo(item);
    }, 280);
  };

  const onItemTap = (item, i) => {
    const allowed = checkPermission ? checkPermission(item) : true;
    if (!allowed) return;
    hapticTick();
    centerItem(i, true);
    goTo(item);
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="producer-dial-nav"
    >
      <div className="relative bg-zinc-950/95 backdrop-blur-xl border-t border-zinc-800/60 overflow-hidden">
        {/* Center notch indicator (like a camera mode dial) */}
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center">
          <div className="w-10 h-[3px] rounded-b bg-gradient-to-r from-violet-500 to-pink-500" />
          <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-pink-500" />
        </div>

        {/* Dial tick marks */}
        <div className="pointer-events-none absolute top-[3px] left-0 right-0 flex justify-between px-3 opacity-25 z-0">
          {Array.from({ length: 36 }).map((_, i) => (
            <span key={i} className={`w-px bg-zinc-500 ${i % 4 === 0 ? 'h-2' : 'h-1'}`} />
          ))}
        </div>

        {/* Fading edges for wheel depth */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-14 bg-gradient-to-r from-zinc-950 via-zinc-950/70 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-l from-zinc-950 via-zinc-950/70 to-transparent z-10" />

        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="flex overflow-x-auto h-[76px] items-end [&::-webkit-scrollbar]:hidden"
          style={{
            scrollSnapType: 'x mandatory',
            paddingLeft: `calc(50% - ${ITEM_W / 2}px)`,
            paddingRight: `calc(50% - ${ITEM_W / 2}px)`,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          {items.map((item, i) => {
            const Icon = item.icon;
            const allowed = checkPermission ? checkPermission(item) : true;
            const badge = item.hasBadge ? (badges[item.hasBadge] || 0) : 0;
            const d = Math.min(Math.abs(i * ITEM_W - scrollX) / ITEM_W, 2.5);
            const scale = Math.max(0.68, 1.18 - 0.26 * d);
            const ty = Math.min(22, 5.5 * d * d);
            const op = Math.max(0.3, 1 - 0.3 * d);
            const isCenter = d < 0.5;
            const label = item.shortLabel || item.label;
            return (
              <button
                key={item.path}
                onClick={() => onItemTap(item, i)}
                className="flex flex-col items-center justify-end flex-shrink-0 outline-none"
                style={{ width: ITEM_W, scrollSnapAlign: 'center' }}
                data-testid={`dial-item-${item.path.split('/').pop()}`}
                aria-label={item.label}
              >
                <div
                  className="flex flex-col items-center gap-1 pb-2 will-change-transform"
                  style={{ transform: `scale(${scale}) translateY(${ty}px)`, opacity: allowed ? op : op * 0.45 }}
                >
                  <div
                    className={`relative flex items-center justify-center w-10 h-10 rounded-full border transition-colors duration-150 ${
                      isCenter && allowed
                        ? 'border-pink-500/70 bg-gradient-to-br from-violet-600/30 to-pink-600/15 text-violet-300 shadow-[0_0_14px_rgba(168,85,247,0.35)]'
                        : 'border-zinc-800 bg-zinc-900/80 text-zinc-500'
                    } ${!allowed ? 'grayscale' : ''}`}
                  >
                    <Icon size={19} strokeWidth={isCenter ? 2.4 : 1.8} />
                    {!allowed && <Lock size={9} className="absolute -bottom-0.5 -right-0.5 text-zinc-600" />}
                    {allowed && badge > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-pink-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                        {badge > 9 ? '9+' : badge}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap ${
                      isCenter && allowed ? 'text-violet-300' : 'text-zinc-500'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default ProducerDialNav;
